import { Paper, styled, useTheme } from "@mui/material";
import { useGetProviderSearchListing } from "apollo/hooks/queries";
import { useProviderSearchContext } from "context/ProviderSearchContext";
import { PROVIDER_SEARCH_PAGES, QUERY_PROGRESS_PENDING } from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { isPrintMode } from "core/model/utils/urls";
import { useSealdContext } from "core/seald/SealdContext";
import { ProviderSearchListing } from "core/types";
import GeneralTable, {
  TableIntro,
} from "ds_legacy/components/Tables/GeneralTable";
import {
  columnProviderSearchCapacity,
  columnProviderSearchContactDetails,
  columnProviderSearchFacility,
  columnProviderSearchLocation,
  columnProviderSearchName,
} from "ds_legacy/components/Tables/GeneralTable/CustomCells/PrintableProviderSearchListing";
import { margin } from "ds_legacy/materials/metrics";
import { usePrintWindow } from "dsl/atoms/PrintButton";
import { useProviderSearchFilterContext } from "dsl/molecules/ProviderSearchAppWrapper";
import { useProviderSearchPageData } from "dsl/molecules/ProviderSearchAppWrapper/PageMetaData";
import { useTranslations } from "translations";

const StyledTablePaper = styled(Paper)`
  @media print {
    margin: ${margin(0, 2)};
    box-shadow: none !important;
  }
`;

export const useProviderSearchCanPrint = (isLoading = false) => {
  const { isLoggedIn } = useProviderSearchContext();
  const { isActive } = useSealdContext();
  const isPrint = isPrintMode();

  // if not print mode, allow children to render
  if (!isPrint) return true;

  // if loading, don't allow children to render
  if (isLoading) return false;

  // if not logged in, allow children to render
  if (!isLoggedIn) return true;

  // if seald is not active, don't allow children to render
  return isActive;
};

const PrintListing = ({
  providerListing,
}: {
  providerListing: ProviderSearchListing | undefined;
}) => {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const theme = useTheme();
  const { currentLocale: locale } = useDateLocale();
  const { config } = useProviderSearchContext();

  usePrintWindow();

  return (
    <StyledTablePaper>
      <TableIntro title={config.printTitle(translations)} />
      {providerListing?.cards?.length ? (
        <GeneralTable
          columns={[
            columnProviderSearchName(),
            columnProviderSearchLocation({ translations }),
            columnProviderSearchContactDetails({ translations }),
            columnProviderSearchFacility({ getOntology, translations }),
            columnProviderSearchCapacity({ locale, theme, translations }),
          ]}
          data={providerListing.cards}
          emptyComponent={null}
          id="provider-listing"
        />
      ) : null}
    </StyledTablePaper>
  );
};

export function PrintableProviderSearchListing() {
  const { filters } = useProviderSearchFilterContext();
  useProviderSearchPageData(PROVIDER_SEARCH_PAGES.PRINT_LISTING);

  const [queryProgress, providerListing] = useGetProviderSearchListing({
    filters,
  });

  const canPrint = useProviderSearchCanPrint(
    queryProgress === QUERY_PROGRESS_PENDING,
  );

  if (!canPrint) return null;

  return <PrintListing providerListing={providerListing} />;
}
